import { storableError } from '../../util/errors';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const lastActivityAt = (tx, messagesById) => {
  const msgRefs = tx.relationships?.messages?.data || [];
  const latestMsgTime = msgRefs.reduce((max, ref) => {
    const msg = messagesById[ref.id.uuid];
    const t = msg?.attributes?.createdAt ? new Date(msg.attributes.createdAt).getTime() : 0;
    return t > max ? t : max;
  }, 0);
  const transitionTime = tx.attributes?.lastTransitionedAt
    ? new Date(tx.attributes.lastTransitionedAt).getTime()
    : 0;
  return Math.max(latestMsgTime, transitionTime);
};

const sortedTransactions = (txs, included = []) => {
  const messagesById = {};
  included.forEach(entity => {
    if (entity.type === 'message') messagesById[entity.id.uuid] = entity;
  });
  return [...txs].sort((a, b) => lastActivityAt(b, messagesById) - lastActivityAt(a, messagesById));
};

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST =
  'app/ReservaAntiguesPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS =
  'app/ReservaAntiguesPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/ReservaAntiguesPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.txs, payload.included);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: null,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = data => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: data,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const fetchAllTransactions = (sdk, queryParams) => {
  const fetchPage = (page, accTxs, accIncluded, allResponses) =>
    sdk.transactions.query({ ...queryParams, page, perPage: 100 }).then(response => {
      const txs = [...accTxs, ...response.data.data];
      const included = [...accIncluded, ...(response.data.included || [])];
      const responses = [...allResponses, response];
      const { totalPages } = response.data.meta;
      return page < totalPages
        ? fetchPage(page + 1, txs, included, responses)
        : { txs, included, responses };
    });
  return fetchPage(1, [], [], []);
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab = 'sales' } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions: getAllTransitionsForEveryProcess(),
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'messages',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    'fields.message': ['createdAt'],
    sort: '-lastTransitionedAt',
  };

  return fetchAllTransactions(sdk, apiQueryParams)
    .then(({ txs, included, responses }) => {
      responses.forEach(r => dispatch(addMarketplaceEntities(r)));
      dispatch(fetchOrdersOrSalesSuccess({ txs, included }));
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
